/**
 * Zod DTOs + response types for the customers domain (spec §5.1/§6.1/§24.3).
 * Shared so the web client validates identically.
 */
import { z } from "zod";
import { CUSTOMER_TYPES } from "../status.js";

export const CUSTOMER_STATUSES = ["active", "archived"] as const;
export type CustomerStatusValue = (typeof CUSTOMER_STATUSES)[number];

export const addressSchema = z.object({
  line1: z.string().trim().min(1).max(120),
  line2: z.string().trim().max(120).optional(),
  postalCode: z.string().trim().min(1).max(12),
  city: z.string().trim().min(1).max(80),
  country: z.string().trim().min(2).max(2).default("CH"),
});
export type AddressDto = z.infer<typeof addressSchema>;

export type AddressResponse = AddressDto;

/** A labelled work site of a customer (e.g. "Garden Seestrasse 4"). */
export const customerSiteSchema = addressSchema.extend({
  id: z.string().uuid().optional(),
  label: z.string().trim().max(80).nullable().optional(),
});
export type CustomerSiteDto = z.infer<typeof customerSiteSchema>;

export interface CustomerSiteResponse extends AddressResponse {
  id: string;
  label?: string;
}

const optionalPhoneSchema = z
  .string()
  .trim()
  .max(40)
  .regex(/^[+\d][\d\s()./-]*$/, "Invalid phone number")
  .optional();
const optionalEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .max(120)
  .optional();

export const customerCreateSchema = z
  .object({
    type: z.enum(CUSTOMER_TYPES).default("private"),
    companyName: z.string().trim().min(1).max(120).optional(),
    salutation: z.string().trim().max(20).optional(),
    firstName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().min(1).max(80),
    phone: optionalPhoneSchema,
    email: optionalEmailSchema,
    preferredLocale: z.string().trim().max(10).optional(),
    notes: z.string().max(4000).optional(),
    billingAddress: addressSchema,
    /** Additional work sites; the billing address is always usable as a site. */
    sites: z.array(customerSiteSchema).max(50).default([]),
  })
  .superRefine((value, ctx) => {
    if (!value.phone && !value.email) {
      const message = "Either a phone number or an email address is required";
      ctx.addIssue({ code: "custom", path: ["phone"], message });
      ctx.addIssue({ code: "custom", path: ["email"], message });
    }
    if (value.type === "company" && !value.companyName) {
      ctx.addIssue({
        code: "custom",
        path: ["companyName"],
        message: "Company name is required for company customers",
      });
    }
  });
export type CustomerCreateDto = z.infer<typeof customerCreateSchema>;

/** Alias kept for the frozen contract name (spec DTO list). */
export const customerUpsertSchema = customerCreateSchema;

export const customerUpdateSchema = z.object({
  version: z.number().int().min(1),
  type: z.enum(CUSTOMER_TYPES).optional(),
  companyName: z.string().trim().min(1).max(120).nullable().optional(),
  salutation: z.string().trim().max(20).nullable().optional(),
  firstName: z.string().trim().max(80).optional(),
  lastName: z.string().trim().min(1).max(80).optional(),
  phone: optionalPhoneSchema.nullable(),
  email: optionalEmailSchema.nullable(),
  preferredLocale: z.string().trim().max(10).nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
  status: z.enum(CUSTOMER_STATUSES).optional(),
  billingAddress: addressSchema.optional(),
  /** Full replacement of the site list; entries with `id` are kept/updated. */
  sites: z.array(customerSiteSchema).max(50).optional(),
});

export const customerListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().max(120).optional(),
  status: z.enum(CUSTOMER_STATUSES).default("active"),
  type: z.enum(CUSTOMER_TYPES).optional(),
  sort: z.enum(["name", "number", "createdAt"]).default("name"),
  sortOrder: z.enum(["asc", "desc"]).default("asc"),
});
export type CustomerListQuery = z.infer<typeof customerListQuerySchema>;
export type CustomerUpdateDto = z.infer<typeof customerUpdateSchema>;

export interface CustomerResponse {
  id: string;
  number: string;
  type: string;
  companyName?: string;
  salutation?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  preferredLocale?: string;
  notes?: string;
  status: CustomerStatusValue;
  displayName: string;
  billingAddress?: AddressResponse;
  sites: CustomerSiteResponse[];
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerDetailResponse extends CustomerResponse {
  counts: {
    requests: number;
    offers: number;
    invoices: number;
    projects: number;
  };
  projects: Array<{
    id: string;
    number: string;
    title: string;
    status: string;
    plannedStart: string | null;
    plannedEnd: string | null;
  }>;
}

export type CustomerDuplicateMatch = "email" | "phone" | "name_postalCode";

export interface CustomerDuplicate {
  id: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  number: string;
  matchedOn: CustomerDuplicateMatch[];
}

export interface CustomerActivityEntry {
  type: "request" | "offer" | "invoice" | "status";
  id: string;
  number?: string;
  status?: string;
  fromStatus?: string;
  toStatus?: string;
  comment?: string;
  userId?: string;
  createdAt: string;
}
